import { useCallback, useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { clearProctorReturn } from '@entities/olympiad'
import { apiClient, doRefresh } from '@shared/api/client'
import { endpoints } from '@shared/api/endpoints'
import { QUERY_CACHE_KEYS } from '@shared/api/queryKeys'
import { AuthContext } from '@shared/auth/authContext'
import { authTokens, type StoredUser } from '@shared/auth/authTokens'
import { ROLES, type UserRole } from '@shared/config/constants'
import { ROUTES } from '@shared/config/routes'

interface LoginResponse {
  access: string
  refresh: string
  user: StoredUser & { profile: StoredUser['profile'] | null }
}

interface ProfilePayload {
  role?: UserRole
  [key: string]: unknown
}

const PROFILE_ENDPOINTS: Array<{ role: UserRole; path: string }> = [
  { role: ROLES.ADMIN, path: endpoints.profile.admin },
  { role: ROLES.REPRESENTATIVE, path: endpoints.profile.representative },
  { role: ROLES.PARTICIPANT, path: endpoints.profile.participant },
]

// Some users (notably admins) come back from /users/token/ with profile=null.
// Probe the role-specific profile endpoints in order; the first one that
// resolves wins and tells us the role + full profile payload.
const PROFILE_RESOLVE_FAILED_MESSAGE = 'Could not determine user role'

async function resolveProfile(): Promise<{ role: UserRole; profile: ProfilePayload } | null> {
  for (const { role, path } of PROFILE_ENDPOINTS) {
    try {
      const { data } = await apiClient.get<ProfilePayload>(path)
      return { role, profile: { ...data, role } }
    } catch {
      // wrong role — try the next one
    }
  }
  return null
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()
  const [initialized, setInitialized] = useState(false)

  // On mount: refresh access token, then restore user from localStorage
  useEffect(() => {
    const init = async () => {
      if (authTokens.getRefresh()) {
        try {
          await doRefresh()
          const storedUser = authTokens.getUser()
          if (storedUser) {
            queryClient.setQueryData(QUERY_CACHE_KEYS.currentUser, storedUser)
          } else {
            // user_data missing (e.g. older session) — build minimal user from stored role
            const role = authTokens.getRole()
            if (role) {
              const fallbackUser: StoredUser = { profile: { role } }
              authTokens.setUser(fallbackUser)
              queryClient.setQueryData(QUERY_CACHE_KEYS.currentUser, fallbackUser)
            }
          }
        } catch (e) {
          // Only clear tokens for genuine auth failures (expired/invalid token),
          // not network errors — a flaky connection shouldn't log the user out
          const status = (e as { status?: number }).status
          if (status === 401 || status === 400) {
            authTokens.clear()
          }
        }
      }
      setInitialized(true)
    }
    init()
  }, [queryClient])

  const { data: user = null } = useQuery<StoredUser | null>({
    queryKey: QUERY_CACHE_KEYS.currentUser,
    queryFn: () => null,
    staleTime: Infinity,
    gcTime: Infinity,
    enabled: false,
  })

  const login = useCallback(
    async (email: string, password: string): Promise<UserRole> => {
      const { data } = await apiClient.post<LoginResponse>(endpoints.auth.token, {
        email,
        password,
      })

      authTokens.setAccess(data.access)
      authTokens.setRefresh(data.refresh)

      // Token payload may have profile=null (e.g. admin accounts). In that
      // case, probe the role-specific profile endpoints to recover the role.
      let role: UserRole | undefined = data.user.profile?.role
      let profile = data.user.profile
      if (!role) {
        const resolved = await resolveProfile()
        if (!resolved) {
          authTokens.clear()
          throw Object.assign(new Error(PROFILE_RESOLVE_FAILED_MESSAGE), { status: 403 })
        }
        role = resolved.role
        profile = { ...(profile ?? {}), ...resolved.profile, role }
      }

      const fullUser: StoredUser = { ...data.user, profile: profile as StoredUser['profile'] }

      authTokens.setRole(role)
      authTokens.setUser(fullUser)
      queryClient.setQueryData(QUERY_CACHE_KEYS.currentUser, fullUser)

      return role
    },
    [queryClient]
  )

  const logout = useCallback(() => {
    authTokens.clear()
    // Контекст возврата из прокторинга — не часть сессии, но принадлежит
    // ушедшему участнику. Чужой он и так не отдаётся (запись подписана
    // владельцем), но держать его до конца вкладки незачем.
    clearProctorReturn()
    queryClient.removeQueries({ queryKey: QUERY_CACHE_KEYS.currentUser })
    window.location.href = ROUTES.LOGIN
  }, [queryClient])

  const role = user?.profile?.role ?? null

  return (
    <AuthContext.Provider
      value={{ user, role, isAuthenticated: !!user, isLoading: !initialized, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  )
}
